export const baseVertex = /* glsl */ (intensity, classification, gradient, userIdChannel) => `
uniform float size;
uniform float scale;

${intensity ? "attribute float Intensity;" : ""}
${classification ? "attribute float Classification;" : ""}

${intensity ? "varying float IntensityV;" : ""}
${classification ? "varying float ClassificationV;" : ""}
${gradient ? "varying vec3 PositionV;" : ""}

${userIdChannel ? "attribute uint pickingUserId;" : ""}
${userIdChannel ? "flat varying uint vPickingUserId;" : ""}

#include <common>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

void main() {

    #include <color_vertex>
    #include <begin_vertex>
    #include <morphtarget_vertex>
    #include <project_vertex>

    gl_PointSize = size;
    
    ${intensity ? "IntensityV = Intensity;" : ""}
    ${classification ? "ClassificationV = Classification;" : ""}
    ${gradient ? "PositionV = position;" : ""};
    ${userIdChannel ? "vPickingUserId = pickingUserId;" : ""}

    #ifdef USE_SIZEATTENUATION

        bool isPerspective = isPerspectiveMatrix( projectionMatrix );

        if ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );

    #endif

    #include <logdepthbuf_vertex>
    #include <clipping_planes_vertex>
    #include <worldpos_vertex>
    #include <fog_vertex>

}
`;
export const baseFragment = (userIdChannel) => /* glsl */ `
layout(location = 1) out highp vec4 pc_pickingBuffer1;
layout(location = 2) out highp vec4 pc_pickingBuffer2;

#define gl_FragColor pc_fragColor

uniform vec3 diffuse;
uniform float opacity;
uniform uint objectId;
uniform uint extraId;
uniform float brightness;
uniform float contrast;
uniform float gamma;

${userIdChannel ? "flat varying uint vPickingUserId;" : ""}

#include <common>
#include <color_pars_fragment>
#include <map_particle_pars_fragment>
#include <alphatest_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

    #include <clipping_planes_fragment>

    vec3 outgoingLight = vec3( 0.0 );
    vec4 diffuseColor = vec4( diffuse, opacity );

    #include <logdepthbuf_fragment>
    #include <map_particle_fragment>
    #include <color_fragment>
    #include <alphatest_fragment>

    // Apply brightness, contrast, and gamma correction
    vec3 color = contrast * diffuseColor.rgb + brightness;
    outgoingLight = vec3(
        clamp(pow(color.r, gamma), 0.0, 1.0),
        clamp(pow(color.g, gamma), 0.0, 1.0),
        clamp(pow(color.b, gamma), 0.0, 1.0)
    );
    
    #include <output_fragment>
    #include <tonemapping_fragment>
    #include <encodings_fragment>
    #include <fog_fragment>
    #include <premultiplied_alpha_fragment>

    uint finalExtraId = uint(0);
    ${userIdChannel ? "finalExtraId = vPickingUserId;" : "finalExtraId = extraId;"}
    pc_pickingBuffer1 = vec4(
        float(objectId >> uint(0)  & uint(0xFF)) / 255.0,
        float(objectId >> uint(8)  & uint(0xFF)) / 255.0,
        float(objectId >> uint(16) & uint(0xFF)) / 255.0,
        float(objectId >> uint(24) & uint(0xFF)) / 255.0
    );
    pc_pickingBuffer2 = vec4(
        float(finalExtraId >> uint(0)  & uint(0xFF)) / 255.0,
        float(finalExtraId >> uint(8)  & uint(0xFF)) / 255.0,
        float(finalExtraId >> uint(16) & uint(0xFF)) / 255.0,
        float(finalExtraId >> uint(24) & uint(0xFF)) / 255.0
    );
}
`;
export const intensityFragment = /* glsl */ (colors, userIdChannel) => {
    // Generate conditions to interpolate along the color spectrum
    let conditions = ``;
    for (let i = 0; i < colors.length - 1; i++) {
        const curr = colors[i];
        const next = colors[i + 1];
        // Upper and lower t-bounds
        const lower = (i / (colors.length - 1)).toFixed(20);
        const upper = ((i + 1) / (colors.length - 1)).toFixed(20);
        conditions += `
        if (gradient >= ${lower} && gradient <= ${upper}) {
            float a = (gradient - ${lower}) / (${upper} - ${lower});
            color = mix(vec3(${curr.r}, ${curr.g}, ${curr.b}),
                        vec3(${next.r}, ${next.g}, ${next.b}),
                        a);
        }
        `;
    }
    return `
    layout(location = 1) out highp vec4 pc_pickingBuffer1;
    layout(location = 2) out highp vec4 pc_pickingBuffer2;

    uniform vec3 diffuse;
    uniform float opacity;
    uniform uint objectId;
    uniform uint extraId;

    varying float IntensityV;
    ${userIdChannel ? "flat varying uint vPickingUserId;" : ""}

    #include <common>
    #include <color_pars_fragment>
    #include <map_particle_pars_fragment>
    #include <alphatest_pars_fragment>
    #include <fog_pars_fragment>
    #include <logdepthbuf_pars_fragment>
    #include <clipping_planes_pars_fragment>

    void main() {

        #include <clipping_planes_fragment>

        vec3 outgoingLight = vec3( 0.0 );
        vec4 diffuseColor = vec4( diffuse, opacity );
        
        #include <logdepthbuf_fragment>
        #include <map_particle_fragment>
        #include <color_fragment>
        #include <alphatest_fragment>

        float gradient = IntensityV / 65535.0;
        vec3 color = vec3( 0.3, 0.3, 0.3 );
        ${conditions}
        outgoingLight = color;

        #include <output_fragment>
        #include <tonemapping_fragment>
        #include <encodings_fragment>
        #include <fog_fragment>
        #include <premultiplied_alpha_fragment>

        uint finalExtraId = uint(0);
        ${userIdChannel ? "finalExtraId = vPickingUserId;" : "finalExtraId = extraId;"}
        pc_pickingBuffer1 = vec4(
            float(objectId >> uint(0)  & uint(0xFF)) / 255.0,
            float(objectId >> uint(8)  & uint(0xFF)) / 255.0,
            float(objectId >> uint(16) & uint(0xFF)) / 255.0,
            float(objectId >> uint(24) & uint(0xFF)) / 255.0
        );
        pc_pickingBuffer2 = vec4(
            float(extraId >> uint(0)  & uint(0xFF)) / 255.0,
            float(extraId >> uint(8)  & uint(0xFF)) / 255.0,
            float(extraId >> uint(16) & uint(0xFF)) / 255.0,
            float(extraId >> uint(24) & uint(0xFF)) / 255.0
        );
    }
    `;
};
export const classificationFragment = (colors, userIdChannel) => /* prettier-ignore */ `
layout(location = 1) out highp vec4 pc_pickingBuffer1;
layout(location = 2) out highp vec4 pc_pickingBuffer2;

#define gl_FragColor pc_fragColor

uniform float opacity;
uniform uint objectId;
uniform uint extraId;

varying float ClassificationV;

#include <common>
#include <color_pars_fragment>
#include <map_particle_pars_fragment>
#include <alphatest_pars_fragment>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {

    #include <clipping_planes_fragment>

    vec3 outgoingLight = vec3( 0.0 );
    vec4 diffuseColor = vec4( 0.0 );
    int c = int(ClassificationV);
    if (c == 0 && ${colors[0].show}) diffuseColor = vec4( ${colors[0].base.r}, ${colors[0].base.g}, ${colors[0].base.b}, opacity );
    else if (c == 1 && ${colors[1].show}) diffuseColor = vec4( ${colors[1].base.r}, ${colors[1].base.g}, ${colors[1].base.b}, opacity );
    else if (c == 2 && ${colors[2].show}) diffuseColor = vec4( ${colors[2].base.r}, ${colors[2].base.g}, ${colors[2].base.b}, opacity );
    else if (c == 3 && ${colors[3].show}) diffuseColor = vec4( ${colors[3].base.r}, ${colors[3].base.g}, ${colors[3].base.b}, opacity );
    else if (c == 4 && ${colors[4].show}) diffuseColor = vec4( ${colors[4].base.r}, ${colors[4].base.g}, ${colors[4].base.b}, opacity );
    else if (c == 5 && ${colors[5].show}) diffuseColor = vec4( ${colors[5].base.r}, ${colors[5].base.g}, ${colors[5].base.b}, opacity );
    else if (c == 6 && ${colors[6].show}) diffuseColor = vec4( ${colors[6].base.r}, ${colors[6].base.g}, ${colors[6].base.b}, opacity );
    else if (c == 7 && ${colors[7].show}) diffuseColor = vec4( ${colors[7].base.r}, ${colors[7].base.g}, ${colors[7].base.b}, opacity );
    else if (c == 8 && ${colors[8].show}) diffuseColor = vec4( ${colors[8].base.r}, ${colors[8].base.g}, ${colors[8].base.b}, opacity );
    else if (c == 9 && ${colors[9].show}) diffuseColor = vec4( ${colors[9].base.r}, ${colors[9].base.g}, ${colors[9].base.b}, opacity );
    else if (c == 10 && ${colors[10].show}) diffuseColor = vec4( ${colors[10].base.r}, ${colors[10].base.g}, ${colors[10].base.b}, opacity );
    else if (c == 11 && ${colors[11].show}) diffuseColor = vec4( ${colors[11].base.r}, ${colors[11].base.g}, ${colors[11].base.b}, opacity );
    else if (c == 12 && ${colors[12].show}) diffuseColor = vec4( ${colors[12].base.r}, ${colors[12].base.g}, ${colors[12].base.b}, opacity );
    else if (c == 13 && ${colors[13].show}) diffuseColor = vec4( ${colors[13].base.r}, ${colors[13].base.g}, ${colors[13].base.b}, opacity );
    else if (c == 14 && ${colors[14].show}) diffuseColor = vec4( ${colors[14].base.r}, ${colors[14].base.g}, ${colors[14].base.b}, opacity );
    else if (c == 15 && ${colors[15].show}) diffuseColor = vec4( ${colors[15].base.r}, ${colors[15].base.g}, ${colors[15].base.b}, opacity );
    else if (c == 16 && ${colors[16].show}) diffuseColor = vec4( ${colors[16].base.r}, ${colors[16].base.g}, ${colors[16].base.b}, opacity );
    else if (c == 17 && ${colors[17].show}) diffuseColor = vec4( ${colors[17].base.r}, ${colors[17].base.g}, ${colors[17].base.b}, opacity );
    else if (c == 18 && ${colors[18].show}) diffuseColor = vec4( ${colors[18].base.r}, ${colors[18].base.g}, ${colors[18].base.b}, opacity );
    else discard;

    #include <logdepthbuf_fragment>
    #include <map_particle_fragment>
    #include <alphatest_fragment>

    outgoingLight = diffuseColor.rgb;

    #include <output_fragment>
    #include <tonemapping_fragment>
    #include <encodings_fragment>
    #include <fog_fragment>
    #include <premultiplied_alpha_fragment>

    uint finalExtraId = uint(0);
    ${userIdChannel ? "finalExtraId = vPickingUserId;" : "finalExtraId = extraId;"}
    pc_pickingBuffer1 = vec4(
        float(objectId >> uint(0)  & uint(0xFF)) / 255.0,
        float(objectId >> uint(8)  & uint(0xFF)) / 255.0,
        float(objectId >> uint(16) & uint(0xFF)) / 255.0,
        float(objectId >> uint(24) & uint(0xFF)) / 255.0
    );
    pc_pickingBuffer2 = vec4(
        float(finalExtraId >> uint(0)  & uint(0xFF)) / 255.0,
        float(finalExtraId >> uint(8)  & uint(0xFF)) / 255.0,
        float(finalExtraId >> uint(16) & uint(0xFF)) / 255.0,
        float(finalExtraId >> uint(24) & uint(0xFF)) / 255.0
    );
}
`;
export const gradientFragment = /* glsl */ (colors, userIdChannel) => {
    // Generate conditions to interpolate along the color spectrum
    let conditions = ``;
    for (let i = 0; i < colors.length - 1; i++) {
        const curr = colors[i];
        const next = colors[i + 1];
        // Upper and lower t-bounds
        const lower = (i / (colors.length - 1)).toFixed(20);
        const upper = ((i + 1) / (colors.length - 1)).toFixed(20);
        conditions += `
        if (gradient >= ${lower} && gradient <= ${upper}) {
            float a = (gradient - ${lower}) / (${upper} - ${lower});
            color = mix(vec3(${curr.r}, ${curr.g}, ${curr.b}),
                        vec3(${next.r}, ${next.g}, ${next.b}),
                        a);
        }
        `;
    }
    return `
    layout(location = 1) out highp vec4 pc_pickingBuffer1;
    layout(location = 2) out highp vec4 pc_pickingBuffer2;
    
    #define gl_FragColor pc_fragColor
    
    uniform float opacity;
    uniform uint objectId;
    uniform uint extraId;
    uniform vec3 minGradient;
    uniform vec3 maxGradient;
    
    varying vec3 PositionV;
    
    #include <common>
    #include <color_pars_fragment>
    #include <map_particle_pars_fragment>
    #include <alphatest_pars_fragment>
    #include <fog_pars_fragment>
    #include <logdepthbuf_pars_fragment>
    #include <clipping_planes_pars_fragment>
    
    void main() {
    
        #include <clipping_planes_fragment>
    
        vec3 outgoingLight = vec3( 0.0 );
    
        vec3 u = PositionV - minGradient;
        vec3 v = maxGradient - minGradient;
        float scalarProjection = dot( u, normalize( v ) );
        float gradient = scalarProjection / length( v );
        vec3 color = vec3( 0.3, 0.3, 0.3 );
        ${conditions}
        vec4 diffuseColor = vec4( color, opacity );
    
        #include <logdepthbuf_fragment>
        #include <map_particle_fragment>
        #include <alphatest_fragment>
    
        outgoingLight = diffuseColor.rgb;
    
        #include <output_fragment>
        #include <tonemapping_fragment>
        #include <encodings_fragment>
        #include <fog_fragment>
        #include <premultiplied_alpha_fragment>
    
        uint finalExtraId = uint(0);
        ${userIdChannel ? "finalExtraId = vPickingUserId;" : "finalExtraId = extraId;"}
        pc_pickingBuffer1 = vec4(
            float(objectId >> uint(0)  & uint(0xFF)) / 255.0,
            float(objectId >> uint(8)  & uint(0xFF)) / 255.0,
            float(objectId >> uint(16) & uint(0xFF)) / 255.0,
            float(objectId >> uint(24) & uint(0xFF)) / 255.0
        );
        pc_pickingBuffer2 = vec4(
            float(finalExtraId >> uint(0)  & uint(0xFF)) / 255.0,
            float(finalExtraId >> uint(8)  & uint(0xFF)) / 255.0,
            float(finalExtraId >> uint(16) & uint(0xFF)) / 255.0,
            float(finalExtraId >> uint(24) & uint(0xFF)) / 255.0
        );
    }
    `;
};
